'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from './ui/AppLogo';
import { Menu, X } from 'lucide-react';

export default function PublicNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { id: 'nav-beranda', label: 'Beranda', href: '/' },
    { id: 'nav-cara-kerja', label: 'Cara Kerja', href: '#cara-kerja' },
    { id: 'nav-inspirasi', label: 'Inspirasi', href: '#' },
    { id: 'nav-profesional', label: 'Profesional', href: '#' },
    { id: 'nav-tentang', label: 'Tentang', href: '#' },
  ];

  return (
    <>
      <style>{`
        @keyframes navSlideDown {
          from { transform: translateY(-100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes logoPulse {
          0%, 100% { filter: drop-shadow(0 0 0px rgba(26,86,219,0)); }
          50% { filter: drop-shadow(0 0 8px rgba(26,86,219,0.6)); }
        }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes mobileSlideDown {
          from { opacity: 0; transform: translateY(-12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes glowPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(26,86,219,0); }
          50% { box-shadow: 0 0 18px 2px rgba(26,86,219,0.35); }
        }
        @keyframes borderGlow {
          0%, 100% { border-color: rgba(255,255,255,0.07); }
          50% { border-color: rgba(26,86,219,0.35); }
        }
        .nav-enter {
          animation: navSlideDown 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .logo-animate {
          animation: logoPulse 3s ease-in-out infinite;
          transition: transform 0.3s ease;
        }
        .logo-animate:hover {
          transform: scale(1.08) rotate(-2deg);
        }
        .nav-link-animated {
          position: relative;
          overflow: hidden;
        }
        .nav-link-animated::after {
          content: '';
          position: absolute;
          bottom: 4px;
          left: 50%;
          transform: translateX(-50%) scaleX(0);
          width: 60%;
          height: 2px;
          border-radius: 2px;
          background: linear-gradient(90deg, #1A56DB, #D4006A);
          transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .nav-link-animated:hover::after {
          transform: translateX(-50%) scaleX(1);
        }
        .nav-link-animated:hover {
          color: white !important;
          background: rgba(255,255,255,0.06) !important;
        }
        .cta-glow {
          position: relative;
          overflow: hidden;
          animation: glowPulse 2.5s ease-in-out infinite;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .cta-glow::before {
          content: '';
          position: absolute;
          top: 0; left: -100%;
          width: 60%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent);
          animation: shimmer 2.8s linear infinite;
        }
        .cta-glow:hover {
          transform: translateY(-2px) scale(1.04);
          box-shadow: 0 6px 24px rgba(26,86,219,0.5);
        }
        .mobile-menu-animate {
          animation: mobileSlideDown 0.3s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .scrolled-border {
          animation: borderGlow 3s ease-in-out infinite;
        }
        .nav-badge-glow {
          animation: shimmer 3s linear infinite;
          background: linear-gradient(90deg, rgba(232,160,32,0.9), rgba(212,0,106,0.9), rgba(232,160,32,0.9));
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>

      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${mounted ? 'nav-enter' : 'opacity-0'} ${scrolled ? 'scrolled-border' : ''}`}
        style={{
          background: scrolled
            ? 'rgba(13,27,42,0.92)'
            : 'transparent',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(255,255,255,0.07)' : 'none',
          boxShadow: scrolled ? '0 4px 32px rgba(0,0,0,0.3), 0 0 0 1px rgba(26,86,219,0.08)' : 'none',
        }}
      >
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-2.5 group logo-animate">
              <AppLogo src="/assets/images/logo_juragan_renov-1787058203675.jpeg" size={32} />
              <div className="flex flex-col leading-none">
                <span className="font-extrabold text-sm tracking-tight text-white transition-all duration-300 group-hover:text-blue-300">Juragan Renov</span>
                <span className="text-[10px] font-medium tracking-widest uppercase nav-badge-glow">2.0</span>
              </div>
            </Link>

            {/* Desktop nav */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks?.map((link, i) => (
                <Link
                  key={link?.id}
                  href={link?.href}
                  className="nav-link-animated px-3.5 py-2 rounded-lg text-sm font-medium transition-all duration-200 text-white/75"
                  style={{
                    animationDelay: `${i * 0.07}s`,
                  }}
                >
                  {link?.label}
                </Link>
              ))}
            </div>

            {/* Desktop CTAs */}
            <div className="hidden lg:flex items-center gap-2.5">
              <Link
                href="/sign-up-login"
                className="nav-link-animated text-sm font-semibold px-4 py-2 rounded-lg transition-all duration-200 text-white/80"
              >
                Masuk
              </Link>
              <Link
                href="/mulai-proyek-wizard"
                className="btn-accent cta-glow text-sm py-2 px-5"
              >
                Mulai Proyek
              </Link>
            </div>

            {/* Mobile toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/08 transition-all duration-200 hover:scale-110 active:scale-95"
            >
              <span className={`block transition-all duration-300 ${mobileOpen ? 'rotate-90' : 'rotate-0'}`}>
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </span>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div
            className="lg:hidden border-t mobile-menu-animate"
            style={{
              background: 'rgba(13,27,42,0.97)',
              backdropFilter: 'blur(20px)',
              borderColor: 'rgba(255,255,255,0.07)',
            }}
          >
            <div className="px-4 py-4 space-y-1">
              {navLinks?.map((link, i) => (
                <Link
                  key={`mobile-${link?.id}`}
                  href={link?.href}
                  className="block px-4 py-2.5 rounded-lg text-sm font-medium text-white/75 hover:text-white transition-all duration-200 hover:bg-white/08 hover:translate-x-1"
                  style={{ animationDelay: `${i * 0.05}s` }}
                  onClick={() => setMobileOpen(false)}
                >
                  {link?.label}
                </Link>
              ))}
              <div className="pt-3 border-t flex flex-col gap-2" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                <Link href="/sign-up-login" className="btn-secondary text-sm" style={{ color: 'white', borderColor: 'rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.06)' }}>
                  Masuk
                </Link>
                <Link href="/mulai-proyek-wizard" className="btn-accent cta-glow text-sm">Mulai Proyek</Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}